import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, enableProdMode, inject, isDevMode, provideAppInitializer, signal } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';

import { CoreEffects } from '@next/core-lib';
import { provideAgrAdvisorRelationshipsSelector } from '@next/core-lib/advisor-relationships-selector';
import { provideAgrCore } from '@next/core-lib/app';
import { I18nBrowserEffects, provideAgrBrowser } from '@next/core-lib/browser';
import { provideAgrCart } from '@next/core-lib/cart';
import { provideAgrLocationSummary } from '@next/core-lib/components';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { FeatureFlagService } from '@next/core-lib/featureflag';
import { AgrNumberDirective } from '@next/core-lib/form';
import { AgrDatePipe, AgrNumberPipe, I18nEffects, LOCALIZED_LINK_CONFIG, provideAgrI18nShared } from '@next/core-lib/i18n';
import { NavigationEffects, provideAgrNavigation } from '@next/core-lib/navigation';
import { RemoveSpecialCharactersPipe } from '@next/core-lib/pipes';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { provideAgrSettings } from '@next/core-lib/settings';
import { SiteEffects, provideAgrSite } from '@next/core-lib/site';
import { provideAgrTable } from '@next/core-lib/table';
import { provideAgrWrapperSiteSidenav } from '@next/core-lib/wrapper-site-sidenav';
import { provideEffects } from '@ngrx/effects';
import { Action, ActionReducer, MetaReducer, provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { localStorageSync } from 'ngrx-store-localstorage';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { CartHttpInterceptor } from './app/cart/cart.interceptor';
import { CartEffects } from './app/cart/store/effects';
import { PreferencesEffects } from './app/components/preferences/store/preferences.effects';
import { preferencesReducer } from './app/components/preferences/store/preferences.reducer';
import { provideIconInitializer } from './app/core/initializers';
import { FormatLabelPipe } from './app/product/pipes/format-label.pipe';
import { ProductEffects } from './app/product/store/effects';
import { expandedSilosReducer, productReducer } from './app/product/store/reducer';
import { AppEffects } from './app/store/effects';
import { appReducer, bannerNotificationReducer } from './app/store/reducer';
import { environment } from './environments/environment';
import { loadRuntimeConfig } from './environments/runtime-config';
import { linkConfig } from './link-config';

const localStorageSyncReducer = <S>(reducer: ActionReducer<S>): ActionReducer<S> =>
  localStorageSync({
    keys: ['banner-notification', 'expandedSilos'],
    rehydrate: true,
    restoreDates: false,
    checkStorageAvailability: true,
  })(reducer);

const metaReducers: Array<MetaReducer<object, Action>> = [localStorageSyncReducer];

const loadedConfig = signal(environment);

export const setupRuntimeConfig = loadRuntimeConfig().then((config) => loadedConfig.set(config));

setupRuntimeConfig.then(() => {
  if (loadedConfig().PRODUCTION) {
    enableProdMode();
  }

  bootstrapApplication(AppComponent, {
    providers: [
      provideRouter(routes),
      // Core provider functions (replacing AgrBrowserModule and AgrAppModule)
      provideAgrBrowser(),
      provideAgrCore(),
      // Feature providers
      provideAgrCart(),
      provideAgrTable(),
      provideAgrWrapperSiteSidenav(),
      provideAgrElasticApi(),
      provideAgrI18nShared(),
      // Icon initialization for LocationSummary component
      provideAgrLocationSummary(),
      // Settings service and store
      provideAgrSettings(),
      // AdvisorRelationshipsSelector service
      provideAgrAdvisorRelationshipsSelector(),
      // Site and Navigation state
      provideAgrSite(),
      provideAgrNavigation(),
      provideAppInitializer(() => {
        const featureFlagService = inject(FeatureFlagService);
        featureFlagService.initialize('dashboard');
      }),
      provideHttpClient(withInterceptorsFromDi()),
      provideStore(
        {},
        {
          metaReducers,
          runtimeChecks: {
            strictStateImmutability: false,
            strictActionImmutability: false,
            strictStateSerializability: false,
            strictActionSerializability: false,
          },
        },
      ),
      provideState('app', appReducer),
      provideState('banner-notification', bannerNotificationReducer),
      provideState('product', productReducer),
      provideState('expandedSilos', expandedSilosReducer),
      provideState('preferencesModule', preferencesReducer),
      provideEffects(CoreEffects, I18nEffects, I18nBrowserEffects, NavigationEffects, SiteEffects, CartEffects, AppEffects, ProductEffects, PreferencesEffects),

      provideAgrRouterStore(),
      provideStoreDevtools({
        maxAge: 25,
        logOnly: !isDevMode(),
        autoPause: true,
        trace: false,
        traceLimit: 75,
        connectInZone: false,
      }),
      {
        provide: ENVIRONMENT_MAPPER,
        useFactory: () => loadedConfig(),
      },
      {
        provide: APP_ID,
        useFactory: () => loadedConfig().APPLICATION_NAME,
      },
      AgrNumberDirective,
      {
        provide: HTTP_INTERCEPTORS,
        useClass: CartHttpInterceptor,
        multi: true,
      },
      {
        provide: LOCALIZED_LINK_CONFIG,
        useValue: linkConfig,
      },
      AgrNumberPipe,
      AgrDatePipe,
      FormatLabelPipe,
      RemoveSpecialCharactersPipe,
      provideIconInitializer(),
    ],
  }).catch((err) => console.error(err));
});
